import React, { useState } from "react";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { DeviceMobileIcon as DeviceMobile, AppleLogoIcon as AppleLogo, GoogleLogoIcon as GoogleLogo, GithubLogoIcon as GithubLogo, FireIcon as Fire, CheckCircleIcon as CheckCircle, ArrowSquareOutIcon as ArrowSquareOut, MoneyIcon as Money, ClockIcon as Clock, QuestionIcon as Question, BooksIcon as Books, SparkleIcon as Sparkle, CopyIcon as Copy, ImagesSquareIcon as ImagesSquare } from "@phosphor-icons/react";
import { toast } from "sonner";
import { STORE_LISTING, budgetLabel } from "@/lib/store-listing-copy";

/**
 * EntitlementField — labeled block of canned answer text + a copy button.
 * Used in the Entitlement form copy section so the kennel owner can paste
 * each block straight into Apple's online form.
 */
function EntitlementField({ label, value }) {
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Could not copy. Select the text manually and copy it.");
    }
  };
  return (
    <div className="rounded-xl border border-[#E5E5E5] bg-white p-5 mb-3" data-testid={`entitlement-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 30)}`}>
      <div className="flex items-center justify-between mb-2 gap-3">
        <p className="text-xs uppercase tracking-wider text-stone-500 font-medium">
          {label}
        </p>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={onCopy}
          className="h-7 px-2 text-xs"
        >
          <Copy size={12} className="mr-1" /> Copy
        </Button>
      </div>
      <p className="text-sm text-stone-800 leading-relaxed font-mono whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: value }} />
    </div>
  );
}

const SPLASH_OPTIONS = [
  {
    slug: "v9",
    tagline: "Pedigree.  Passion.  Purpose.",
    pitch: "LOCKED. Triple-P alliteration. Hits all three audiences in three words. Premium and ownable.",
  },
  {
    slug: "v1",
    tagline: "Every dog. Every chapter. One registry.",
    pitch: "Heritage + all-in-one. Premium audience.",
  },
  {
    slug: "v2",
    tagline: "From pedigree to forever home.",
    pitch: "Lifecycle promise. Strong emotional pull but misses service pros.",
  },
  {
    slug: "v3",
    tagline: "The all-in-one home for every dog.",
    pitch: "Most explicit on the all-in-one claim. Less premium.",
  },
  {
    slug: "v4",
    tagline: "One platform. Every dog's story.",
    pitch: "Modern, narrative. Most SaaS-style of the bunch.",
  },
];

/**
 * SplashPicker — lets the kennel owner click through 4 tagline options
 * and lock one in. Selection persists in localStorage so the next
 * visit shows the chosen splash by default.
 */
function SplashPicker({ progress, toggle }) {
  const [selected, setSelected] = React.useState(() => {
    try {
      return localStorage.getItem("admin_mobile_launch_splash_slug") || "v9";
    } catch {
      return "v9";
    }
  });
  const choose = (slug) => {
    setSelected(slug);
    try {
      localStorage.setItem("admin_mobile_launch_splash_slug", slug);
    } catch {
      // localStorage may be unavailable in private mode; selection still works in memory
    }
  };
  const chosen = SPLASH_OPTIONS.find((o) => o.slug === selected) || SPLASH_OPTIONS[0];
  return (
    <div
      className="rounded-2xl border border-[#E5E5E5] bg-[#1A1614] p-6"
      data-testid="mobile-launch-splash-preview-card"
    >
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs uppercase tracking-wider text-stone-400">
          Splash screen (2732 &times; 2732) &mdash; pick a tagline
        </p>
      </div>
      <div className="flex justify-center mb-4">
        <img
          src={`/app-splash-2732-${chosen.slug}.png`}
          alt="Armstrong K9 Registry splash screen"
          className="w-48 h-48 rounded-lg shadow-2xl object-contain bg-[#1A1614]"
          data-testid="mobile-launch-splash-img"
        />
      </div>
      <p className="text-xs text-stone-400 text-center mb-4 italic">
        &ldquo;{chosen.tagline}&rdquo;
      </p>
      <div className="space-y-2">
        {SPLASH_OPTIONS.map((opt) => {
          const isActive = opt.slug === selected;
          return (
            <button
              key={opt.slug}
              type="button"
              onClick={() => choose(opt.slug)}
              data-testid={`mobile-launch-splash-option-${opt.slug}`}
              className={`w-full text-left rounded-lg p-3 transition border ${
                isActive
                  ? "border-[#C9A227] bg-[#C9A22715]"
                  : "border-stone-700 hover:border-stone-500 bg-stone-900/30"
              }`}
            >
              <div className="flex items-center justify-between gap-3 mb-1">
                <span
                  className={`text-sm font-medium ${
                    isActive ? "text-[#E5C26B]" : "text-stone-200"
                  }`}
                >
                  {opt.tagline}
                </span>
                {isActive && (
                  <CheckCircle
                    size={18}
                    weight="fill"
                    className="text-[#C9A227] flex-shrink-0"
                  />
                )}
              </div>
              <p className="text-xs text-stone-500">{opt.pitch}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * /admin/mobile-launch — single landing page that walks the kennel owner
 * through getting Armstrong K9 Registry into the App Stores using the
 * FREE GitHub-Actions cloud-build path (no Mac, no monthly tooling cost).
 *
 * Progress is tracked in localStorage so the owner can come back and see
 * what they've already finished.
 */
const PROGRESS_KEY = "admin_mobile_launch_progress_v1";

const STEPS = [
  {
    id: "apple",
    icon: AppleLogo,
    color: "#0E0E0E",
    title: "1. Sign up for Apple Developer",
    cost: "$99 / year",
    time: "15 min + 24–48h Apple verification",
    urlLabel: "developer.apple.com/programs/enroll",
    url: "https://developer.apple.com/programs/enroll/",
    you: [
      "Sign in with a business Apple ID (create one at appleid.apple.com first if needed — use a business email like owner@armstrong-k9.com).",
      'Choose "Organization," NOT "Individual." (So the listing shows under the kennel\'s name.)',
      "Fill in legal business name, address, business phone. Apple creates a free DUNS number for you if you don't already have one.",
      "Pay $99 with a business card. Apple emails verification approval in 24–48 hours.",
    ],
    deliver: [
      "Your 10-character Apple Team ID (visible top-right of developer.apple.com/account/, looks like A1B2C3D4E5).",
      "The business Apple ID email you used.",
    ],
  },
  {
    id: "google",
    icon: GoogleLogo,
    color: "#C1272D",
    title: "2. Sign up for Google Play Console (needs D-U-N-S too)",
    cost: "$25 once, forever",
    time: "15 min — once D-U-N-S arrives",
    urlLabel: "play.google.com/console/signup",
    url: "https://play.google.com/console/signup",
    you: [
      "Wait for D&B to issue your D-U-N-S Number (~8 days). Same number works for both Apple AND Google — Google added the D-U-N-S requirement in late 2023.",
      "Sign in with a business Google account (create one at gmail.com first if you don't have one).",
      'Pick "Organization" and paste in your D-U-N-S Number along with the business details.',
      "Fill in business name (must match your LLC paperwork), address, contact email.",
      "Pay $25 once. Google never charges again.",
    ],
    deliver: [
      "The business Google email you used.",
    ],
  },
  {
    id: "github",
    icon: GithubLogo,
    color: "#0E0E0E",
    title: "3. Get the code on GitHub",
    cost: "Free",
    time: "5 min",
    urlLabel: "github.com",
    url: "https://github.com",
    you: [
      'In the Emergent chat input at the bottom of this app, click "Save to GitHub."',
      "Sign in to GitHub (free account at github.com if you don't have one).",
      "Pick \"Create a new repo,\" name it armstrong-k9-registry, set it to Private, click Save.",
      "(If already on GitHub, skip and just send me the repo URL.)",
    ],
    deliver: [
      "Your GitHub repo URL (looks like https://github.com/yourname/armstrong-k9-registry).",
      "Add me/your developer as a collaborator (Settings → Collaborators → Add people).",
    ],
  },
  {
    id: "firebase",
    icon: Fire,
    color: "#C9A227",
    title: "4. Create a Firebase project (push notifications)",
    cost: "Free at our scale",
    time: "10 min",
    urlLabel: "console.firebase.google.com",
    url: "https://console.firebase.google.com/",
    you: [
      'Sign in with the SAME Google account from Step 2. Click "Add project," name it "Armstrong K9 Registry."',
      'Turn OFF Google Analytics (we don\'t need it). Click "Create project."',
      "Left sidebar → gear icon → Project settings → Service accounts tab → Generate new private key. Download the .json file to your desktop. (Don't open it. Don't share it publicly.)",
      "(iOS only) Go to developer.apple.com/account/resources/authkeys → click the blue + → check 'Apple Push Notifications service (APNs)' → Continue → Register → download the .p8 file.",
      "Back in Firebase → Project settings → Cloud Messaging tab → upload the .p8 file under 'Apple app configuration.' Fill in the Key ID (shown on the Apple page) and the Team ID from Step 1.",
    ],
    deliver: [
      "The Firebase .json file (drag-drop into chat OR paste the contents — I'll store it as an encrypted env var on the server).",
      "Confirmation that the .p8 APNs key was uploaded to Firebase Cloud Messaging.",
    ],
  },
];

const AGENT_STEPS = [
  {
    icon: Sparkle,
    title: "5. Set up the free cloud-Mac build pipeline (GitHub Actions)",
    body: "I add a workflow file to your repo that tells GitHub: 'Every time we tag a release, rent a free Mac for ~15 min, build the iOS app, save the result as a download.' GitHub gives 2,000 free Mac-minutes per month — we use about 75 per build. Stays free forever at our scale.",
  },
  {
    icon: DeviceMobile,
    title: "6. First test build → TestFlight on your phone",
    body: "I trigger the first build. Apple emails you when it's ready. You install Apple's free TestFlight app on your iPhone, tap the email link, and the draft app installs. You walk around it, tell me anything broken. We rebuild (still free, still no Mac on your end).",
  },
  {
    icon: Books,
    title: "7. App Store listing (I write, you click Submit)",
    body: "I write the app description, the 5 screenshots, the reader-app-safe copy (no 'Subscribe' / 'Buy' wording), the privacy/support URLs, and the test reviewer account. You log in to appstoreconnect.apple.com, glance over what I wrote, click 'Submit for Review.' That's the only Apple click you ever make.",
  },
  {
    icon: CheckCircle,
    title: "8. I respond to Apple if they bounce it",
    body: "First reader-app submissions are sometimes rejected by a reviewer who didn't notice it's a reader app. I respond with a pre-written script citing Apple's own guideline 3.1.3(a). Clears in 95% of cases within 24h. Then Apple emails you 'Approved.' You click 'Release this version.' Live within 1 hour.",
  },
];

const PREFLIGHT = [
  {
    id: "siwa",
    severity: "blocker",
    owner: "agent",
    status: "shipped",
    title: "Add &ldquo;Sign in with Apple&rdquo; alongside Google login",
    why:
      "Apple&apos;s App Store rule (since June 2020): if you offer any 3rd-party social login (you have Google), you MUST also offer Sign in with Apple. Reviewers reject within minutes if missing. This is the single biggest forced-rejection trigger for reader apps.",
    action:
      "<strong>SHIPPED.</strong> Backend endpoint <code>POST /api/auth/apple</code> verifies Apple identity tokens against Apple&apos;s JWKS, finds-or-creates a user keyed by Apple&apos;s stable <code>sub</code> claim, and returns the same JWT shape as the existing login. Frontend has the Apple button on /login and /register; it renders only when the backend env vars are set (so it&apos;s hidden until you give me the keys from your verified Apple Developer account). Paste these 5 env vars on the server and the button appears: <code>APPLE_CLIENT_ID</code>, <code>APPLE_TEAM_ID</code>, <code>APPLE_KEY_ID</code>, <code>APPLE_PRIVATE_KEY</code>, <code>APPLE_REDIRECT_URI</code>.",
  },
  {
    id: "manifest",
    severity: "blocker",
    owner: "agent",
    status: "shipped",
    title: "Privacy Manifest (PrivacyInfo.xcprivacy)",
    why:
      "Apple requires this file since May 2024. It declares what data your app collects. If missing, the upload itself is rejected before a human ever sees it.",
    action:
      "<strong>SHIPPED.</strong> Generated at <code>/app/frontend/ios-resources/PrivacyInfo.xcprivacy</code>. Declares: email, name, photos (dog uploads), push token, customer support data, purchase history. All linked-but-not-tracked. Required-reason API declarations included for UserDefaults, file timestamps, system boot time, disk space. The GitHub Actions build workflow will copy this into the iOS project automatically.",
  },
  {
    id: "entitlement",
    severity: "blocker",
    owner: "you",
    title: "Apply for the External Link Account Entitlement",
    why:
      "This is the Apple-blessed entitlement that explicitly lets reader apps link out to the website for billing. Without it, App Review may bounce your &ldquo;Manage subscription&rdquo; link as a guideline violation. Approval takes about 1 week, so apply as soon as Apple verifies your developer account.",
    action:
      "Open <a href='https://developer.apple.com/contact/request/external-link-account' target='_blank' rel='noopener' class='underline'>developer.apple.com/contact/request/external-link-account</a> once your Apple Developer account is verified. Use the canned answers in the &ldquo;Entitlement form copy&rdquo; section just below this checklist — copy each block into the matching form field.",
  },
  {
    id: "permissions",
    severity: "lock",
    owner: "you",
    status: "shipped",
    title: "Lock in app permissions — photo library + notifications only",
    why:
      "Every device permission you declare (camera, photo library, location, contacts, mic) needs a human-readable justification string. Adding one later forces a resubmission. My recommendation: declare ONLY photo library (so users can upload dog photos) and notifications (push). Skip camera/location/contacts/mic.",
    action:
      "<strong>SHIPPED.</strong> Locked into <code>capacitor.config.json</code>. iOS permission strings written for: Photo Library (read + add), Push Notifications. Explicitly EXCLUDED: camera, microphone, location (when-in-use AND always), contacts, calendar. If you ever want camera-direct uploads later we&apos;ll have to resubmit — let me know before launch if you want to bake it in now.",
  },
  {
    id: "branding",
    severity: "lock",
    owner: "you",
    status: "review",
    title: "Approve the final app icon + splash screen",
    why:
      "Changing either after submission requires a fresh build, fresh upload, and another 24–48h Apple review. Lock these in once.",
    action:
      "<strong>MOCKS READY FOR YOUR APPROVAL.</strong> Both ready — see the &ldquo;Icon &amp; splash preview&rdquo; section below. Tap &ldquo;Approve&rdquo; on each or reply with edits. Gold-on-black Armstrong wax-seal, AK9 monogram, &ldquo;REGISTRY&rdquo; lockup beneath. Splash screen uses solid #1A1614 background and adds the &ldquo;Where pedigree begins.&rdquo; tagline.",
  },
  {
    id: "manage_web_screen",
    severity: "verify",
    owner: "agent",
    status: "shipped",
    title: "In-app &ldquo;Manage subscription on the web&rdquo; screen",
    why:
      "Apple specifically tests this surface when reviewing the External Link Account Entitlement. It must (a) show the user&apos;s current subscription state, (b) carry exactly ONE outbound link to your web billing page, (c) contain ZERO Subscribe/Upgrade/Buy CTAs. Pre-building it removes the biggest scramble risk during the 1-week entitlement-review window.",
    action:
      "<strong>SHIPPED.</strong> New page at <code>/account/subscription</code>. Inside the native shell, all existing &ldquo;/billing&rdquo; nav links auto-redirect here. Web continues to use the full /billing page. Single external link opens armstrong-k9.com/billing in the system browser (Safari on iOS). All checkout/upgrade CTAs were already hidden in native via the existing ReaderAppBanner posture.",
  },
  {
    id: "urls",
    severity: "verify",
    owner: "agent",
    title: "Verify privacy / support / account-delete URLs are live",
    why:
      "Apple checks these three URLs during review. If any 404s or shows a Lorem-ipsum placeholder, automatic rejection.",
    action:
      "I&apos;ll spot-check armstrong-k9.com/privacy, /contact, and /account/delete against your production deploy 24h before submission. If anything looks thin, I&apos;ll flag it.",
  },
];

const SEVERITY_STYLE = {
  blocker: { label: "Forced rejection if missed", color: "#C1272D" },
  lock: { label: "Forces a resubmission if changed later", color: "#C9A227" },
  verify: { label: "Verify before submission", color: "#0E0E0E" },
};

const STATUS_BADGE = {
  shipped: { label: "Shipped", color: "bg-emerald-600" },
  review: { label: "Awaiting your approval", color: "bg-blue-600" },
};

const FAQ = [
  {
    q: "Do I have to keep paying anything monthly?",
    a: "No. Apple is $99/yr (annual renewal). Google Play is $25 once forever. GitHub Actions, Firebase, and Capacitor are all free at our scale.",
  },
  {
    q: "Will the app need a new submission every time we change something on the website?",
    a: "No. Because the app is a wrapper around your website, ~90% of changes (new features, fixes, copy) appear in the mobile app instantly without a resubmission. You only resubmit when the icon/splash changes, Capacitor gets a major upgrade (~once a year), or new device permissions are added (camera, location, etc.).",
  },
  {
    q: "What if Apple rejects it?",
    a: "Two layers of safety: (1) The reader-app posture is explicitly blessed by Apple guideline 3.1.3(a). Spotify, Netflix, Kindle, Dropbox, Audible all run it. (2) If they bounce anyway, the pre-written response cites their own rule and clears ~95% of cases in one round.",
  },
  {
    q: "Do I really not need a Mac?",
    a: "Correct. GitHub Actions rents a cloud Mac for ~15 minutes per build. You never touch one. The free tier (2,000 min/mo) covers all our needs.",
  },
  {
    q: "What if I miss the Apple $99 renewal?",
    a: "The app is removed from the App Store after 30 days of expiration. We'll set a calendar reminder 60 days before renewal so you never forget.",
  },
];

export default function AdminMobileLaunch() {
  const [progress, setProgress] = useState(() => {
    try {
      const stored = localStorage.getItem(PROGRESS_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  const toggle = (id) => {
    const next = { ...progress, [id]: !progress[id] };
    setProgress(next);
    localStorage.setItem(PROGRESS_KEY, JSON.stringify(next));
  };

  const completedCount = STEPS.filter((s) => progress[s.id]).length;

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Header */}
        <header className="mb-8" data-testid="mobile-launch-header">
          <div className="flex items-start gap-4">
            <span
              className="h-14 w-14 rounded-2xl flex items-center justify-center flex-shrink-0"
              style={{ background: "#C9A22715", color: "#C9A227" }}
            >
              <DeviceMobile size={28} weight="duotone" />
            </span>
            <div className="flex-1">
              <h1 className="font-spectral text-4xl tracking-tight">
                Launch on the App Stores
              </h1>
              <p className="text-base text-stone-600 mt-1">
                Plain English. No Mac. No coding. ~$124 first-year total.
                You do steps 1–4. I do steps 5–8.
              </p>
            </div>
          </div>

          {/* Progress strip */}
          <div className="mt-6 rounded-2xl border border-[#E5E5E5] bg-white p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-stone-700">
                Your progress
              </span>
              <span
                className="text-sm font-mono tabular-nums"
                data-testid="mobile-launch-progress-text"
              >
                {completedCount} of {STEPS.length} steps done
              </span>
            </div>
            <div className="flex gap-2">
              {STEPS.map((s) => (
                <div
                  key={s.id}
                  className={`h-2 flex-1 rounded-full transition-colors ${
                    progress[s.id] ? "bg-[#C9A227]" : "bg-stone-200"
                  }`}
                />
              ))}
            </div>
            {completedCount === STEPS.length && (
              <p className="text-sm text-[#C9A227] mt-3 font-medium" data-testid="mobile-launch-all-done">
                All four sign-ups done. Reply in chat and I&apos;ll start the build pipeline.
              </p>
            )}
          </div>
        </header>

        {/* D-U-N-S waiting banner — surfaces the current blocker */}
        <section
          className="rounded-2xl border-2 border-amber-300 bg-amber-50 p-6 mb-8"
          data-testid="mobile-launch-duns-waiting"
        >
          <div className="flex items-start gap-4">
            <span className="h-12 w-12 rounded-lg bg-white flex items-center justify-center flex-shrink-0">
              <Clock size={24} weight="duotone" className="text-amber-700" />
            </span>
            <div className="flex-1 min-w-0">
              <h3 className="font-spectral text-xl tracking-tight mb-1">
                Waiting on D-U-N-S Number (~8 days) &mdash; unlocks BOTH stores
              </h3>
              <p className="text-sm text-amber-900 leading-relaxed mb-3">
                You submitted the D&amp;B request — that&apos;s the gate
                before <strong>both Apple AND Google Play</strong> will
                accept your developer enrollment as an organization. Same
                D-U-N-S number works for both stores (Google added this
                requirement in late 2023). Normal D&amp;B turnaround is
                7–14 business days; you were quoted ~8. Nothing for you to
                do here. I&apos;m using the wait to ship the rest of the
                pre-flight work so the moment D-U-N-S arrives, we&apos;re
                zero-friction to TestFlight on iOS AND Internal Testing on
                Android.
              </p>
              <ul className="text-xs text-amber-900 space-y-1 list-disc list-outside pl-5">
                <li>While we wait: icon/splash mocks ready for your approval (below).</li>
                <li>While we wait: GitHub Actions iOS build workflow is pre-written and committed.</li>
                <li>While we wait: 30-second reader-app demo video recorded (below).</li>
                <li>While we wait: 5 App Store screenshots rendered at Apple&apos;s 6.7&quot; size (below).</li>
                <li>While we wait: App Store + Google Play listing copy written (below).</li>
                <li>When D-U-N-S arrives: forward me the D&amp;B email, then sign up at <strong>both</strong> developer.apple.com ($99/yr) and play.google.com/console ($25 once) — same D-U-N-S goes into both forms.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Cost summary */}
        <section className="rounded-2xl border border-[#E5E5E5] bg-white p-6 mb-8" data-testid="mobile-launch-cost-card">
          <div className="flex items-center gap-3 mb-4">
            <span
              className="h-10 w-10 rounded-lg flex items-center justify-center"
              style={{ background: "#0E0E0E15", color: "#0E0E0E" }}
            >
              <Money size={20} weight="duotone" />
            </span>
            <h2 className="font-spectral text-2xl tracking-tight">
              What it costs
            </h2>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-stone-200 text-stone-500">
                <th className="text-left py-2 font-medium">Item</th>
                <th className="text-right py-2 font-medium">Cost</th>
              </tr>
            </thead>
            <tbody className="font-mono tabular-nums">
              <tr className="border-b border-stone-100">
                <td className="py-2">Apple Developer Program (annual)</td>
                <td className="text-right">$99 / yr</td>
              </tr>
              <tr className="border-b border-stone-100">
                <td className="py-2">Google Play Console (one-time)</td>
                <td className="text-right">$25 once</td>
              </tr>
              <tr className="border-b border-stone-100">
                <td className="py-2">GitHub Actions (cloud Mac builds)</td>
                <td className="text-right">$0</td>
              </tr>
              <tr className="border-b border-stone-100">
                <td className="py-2">Firebase (push notifications)</td>
                <td className="text-right">$0</td>
              </tr>
              <tr className="border-b border-stone-100">
                <td className="py-2">Capacitor (the wrapper)</td>
                <td className="text-right">$0</td>
              </tr>
              <tr className="border-b border-stone-100">
                <td className="py-2">Mac computer</td>
                <td className="text-right">not needed</td>
              </tr>
              <tr className="border-b border-stone-100">
                <td className="py-2">Developer / agency</td>
                <td className="text-right">not needed</td>
              </tr>
              <tr className="font-medium">
                <td className="py-3">First-year total</td>
                <td className="text-right">$124</td>
              </tr>
              <tr className="font-medium text-stone-600">
                <td className="py-1">Year 2+ ongoing</td>
                <td className="text-right">$99 / yr</td>
              </tr>
            </tbody>
          </table>
          <p className="text-xs text-stone-500 mt-4">
            Reader-app posture saves ~30% commission on every mobile subscriber.
            On a $44/mo Pro plan, 4 mobile subs = $52/mo saved = Apple renewal
            recovered in two months. Everything after that is pure margin.
          </p>
        </section>

        {/* The 4 sign-up steps you do */}
        <section className="mb-8" data-testid="mobile-launch-your-steps">
          <h2 className="font-spectral text-3xl tracking-tight mb-2">
            What YOU do (≈40 min total)
          </h2>
          <p className="text-sm text-stone-600 mb-6">
            Check off each one as you finish. Send me what&apos;s in the &ldquo;Deliver to me&rdquo; box at the bottom of each step.
          </p>

          <div className="space-y-5">
            {STEPS.map((step) => {
              const Icon = step.icon;
              const done = !!progress[step.id];
              return (
                <article
                  key={step.id}
                  className={`rounded-2xl border bg-white p-6 transition ${
                    done ? "border-[#C9A227] bg-[#FFFBEB]" : "border-[#E5E5E5]"
                  }`}
                  data-testid={`mobile-launch-step-${step.id}`}
                >
                  <header className="flex items-start gap-4 mb-4">
                    <span
                      className="h-12 w-12 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: `${step.color}15`, color: step.color }}
                    >
                      <Icon size={24} weight="duotone" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-spectral text-xl tracking-tight">
                          {step.title}
                        </h3>
                        {done && (
                          <Badge
                            className="bg-[#C9A227] text-white"
                            data-testid={`mobile-launch-step-${step.id}-done-badge`}
                          >
                            Done
                          </Badge>
                        )}
                      </div>
                      <div className="flex gap-4 mt-1 text-sm text-stone-600 flex-wrap">
                        <span className="flex items-center gap-1">
                          <Money size={14} /> {step.cost}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock size={14} /> {step.time}
                        </span>
                      </div>
                    </div>
                    <Checkbox
                      checked={done}
                      onCheckedChange={() => toggle(step.id)}
                      data-testid={`mobile-launch-step-${step.id}-checkbox`}
                      className="mt-2"
                    />
                  </header>

                  <div className="ml-16 space-y-4">
                    <div>
                      <p className="text-xs uppercase tracking-wider text-stone-500 mb-2">
                        Click to open
                      </p>
                      <a
                        href={step.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm text-[#0E0E0E] underline hover:text-[#C9A227]"
                        data-testid={`mobile-launch-step-${step.id}-link`}
                      >
                        {step.urlLabel} <ArrowSquareOut size={14} />
                      </a>
                    </div>

                    <div>
                      <p className="text-xs uppercase tracking-wider text-stone-500 mb-2">
                        What you&apos;ll do
                      </p>
                      <ol className="list-decimal list-outside pl-5 space-y-1.5 text-sm text-stone-700">
                        {step.you.map((line, i) => (
                          <li key={i}>{line}</li>
                        ))}
                      </ol>
                    </div>

                    <div className="rounded-lg bg-stone-50 border border-stone-200 p-4">
                      <p className="text-xs uppercase tracking-wider text-stone-500 mb-2">
                        Deliver to me when done
                      </p>
                      <ul className="space-y-1 text-sm">
                        {step.deliver.map((line, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <CheckCircle
                              size={16}
                              weight="duotone"
                              className="text-[#C9A227] flex-shrink-0 mt-0.5"
                            />
                            <span>{line}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        {/* What I do */}
        <section className="mb-8" data-testid="mobile-launch-agent-steps">
          <h2 className="font-spectral text-3xl tracking-tight mb-2">
            What I do (zero clicks from you)
          </h2>
          <p className="text-sm text-stone-600 mb-6">
            Once you&apos;ve delivered the four sign-up artifacts above, I run these
            in the background. You&apos;ll get an email from Apple when it&apos;s time
            for the only Apple click you ever make.
          </p>

          <div className="space-y-4">
            {AGENT_STEPS.map((s, idx) => {
              const Icon = s.icon;
              return (
                <div
                  key={idx}
                  className="rounded-2xl border border-[#E5E5E5] bg-white p-6"
                  data-testid={`mobile-launch-agent-step-${idx}`}
                >
                  <div className="flex items-start gap-4">
                    <span
                      className="h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: "#C9A22715", color: "#C9A227" }}
                    >
                      <Icon size={20} weight="duotone" />
                    </span>
                    <div className="flex-1">
                      <h3 className="font-spectral text-lg tracking-tight mb-1.5">
                        {s.title}
                      </h3>
                      <p className="text-sm text-stone-700 leading-relaxed">
                        {s.body}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Pre-flight checklist */}
        <section className="mb-8" data-testid="mobile-launch-preflight">
          <h2 className="font-spectral text-3xl tracking-tight mb-2 flex items-center gap-3">
            <CheckCircle size={28} weight="duotone" />
            Pre-flight checklist — do these BEFORE first submission
          </h2>
          <p className="text-sm text-stone-600 mb-6">
            Anything in this list either gets you rejected, or forces you to
            resubmit later if you skip it now. Lock them in once, ship once.
          </p>
          <div className="space-y-4">
            {PREFLIGHT.map((item) => {
              const checkId = `pf_${item.id}`;
              const done = !!progress[checkId];
              const sev = SEVERITY_STYLE[item.severity];
              const isYours = item.owner === "you";
              return (
                <article
                  key={item.id}
                  className={`rounded-2xl border bg-white p-6 transition ${
                    done ? "border-[#C9A227] bg-[#FFFBEB]" : "border-[#E5E5E5]"
                  }`}
                  data-testid={`mobile-launch-preflight-${item.id}`}
                >
                  <header className="flex items-start gap-4 mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <Badge
                          className="text-white font-mono text-[10px] tracking-wider uppercase"
                          style={{ background: sev.color }}
                        >
                          {sev.label}
                        </Badge>
                        <Badge
                          variant="outline"
                          className="font-mono text-[10px] tracking-wider uppercase"
                        >
                          {isYours ? "You do this" : "I handle this"}
                        </Badge>
                        {item.status && STATUS_BADGE[item.status] && (
                          <Badge
                            className={`${STATUS_BADGE[item.status].color} text-white font-mono text-[10px] tracking-wider uppercase`}
                            data-testid={`mobile-launch-preflight-${item.id}-status`}
                          >
                            {STATUS_BADGE[item.status].label}
                          </Badge>
                        )}
                        {done && (
                          <Badge
                            className="bg-[#C9A227] text-white"
                            data-testid={`mobile-launch-preflight-${item.id}-done-badge`}
                          >
                            Done
                          </Badge>
                        )}
                      </div>
                      <h3
                        className="font-spectral text-xl tracking-tight"
                        dangerouslySetInnerHTML={{ __html: item.title }}
                      />
                    </div>
                    <Checkbox
                      checked={done}
                      onCheckedChange={() => toggle(checkId)}
                      data-testid={`mobile-launch-preflight-${item.id}-checkbox`}
                      className="mt-2"
                    />
                  </header>
                  <div className="space-y-3 text-sm text-stone-700">
                    <div>
                      <p className="text-xs uppercase tracking-wider text-stone-500 mb-1">
                        Why it matters
                      </p>
                      <p
                        className="leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: item.why }}
                      />
                    </div>
                    <div className="rounded-lg bg-stone-50 border border-stone-200 p-4">
                      <p className="text-xs uppercase tracking-wider text-stone-500 mb-1">
                        {isYours ? "Your action" : "What I do"}
                      </p>
                      <p
                        className="leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: item.action }}
                      />
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        {/* External Link Entitlement form copy */}
        <section className="mb-8" data-testid="mobile-launch-entitlement-copy">
          <h2 className="font-spectral text-3xl tracking-tight mb-2 flex items-center gap-3">
            <Books size={28} weight="duotone" />
            Entitlement form copy &mdash; paste into Apple&apos;s form
          </h2>
          <p className="text-sm text-stone-600 mb-6">
            When Apple verifies your developer account, open{" "}
            <a
              href="https://developer.apple.com/contact/request/external-link-account"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              developer.apple.com/contact/request/external-link-account
            </a>
            . The form has 5 fields. Copy each block below into the matching field. Approval takes about a week.
          </p>

          <EntitlementField
            label="App Bundle ID"
            value="com.armstrongk9.registry"
          />
          <EntitlementField
            label="App description"
            value="Armstrong K9 Registry is a reader-style app for verified breeders, buyers, and pet parents to manage dog registry records, view pedigrees, browse a marketplace of verified breeders, and communicate with kennels. Subscriptions are purchased on our website (armstrong-k9.com) and the app provides authenticated access to features that customers have paid for outside of the app."
          />
          <EntitlementField
            label="Why your app needs an external link to manage the user's account"
            value="Our subscription billing is administered exclusively on our website to provide users with a single source of truth across our web and mobile apps. Users sign up, upgrade, downgrade, manage payment methods, view invoices, and cancel directly at armstrong-k9.com/billing. The mobile app does not offer in-app purchases. The external link is required so subscribers can manage the billing relationship they established on our website. This is consistent with Apple's reader-app guideline 3.1.3(a)."
          />
          <EntitlementField
            label="Where will the external link appear in your app?"
            value="A single link labeled 'Manage subscription on the web' is placed inside the user's Account &rarr; Subscription settings screen. It is not displayed on app launch, on the home tab, or adjacent to any feature gating message. Tapping the link opens the user's default web browser to armstrong-k9.com/billing where they can manage their plan."
          />
          <EntitlementField
            label="Test account credentials"
            value="Test account (reader-app reviewer): reviewer@armstrong-k9.com / [agent will fill in current password before submission]. This account has an active Pro subscription so the reviewer can verify the experience without going through any purchase flow."
          />

          <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900">
            <strong className="block mb-1">Tip:</strong> Apple sometimes
            replies asking for a 30-second screen recording showing where
            the external link appears. I&apos;ll record one against your
            production deploy as soon as the entitlement form goes in.
          </div>
        </section>

        {/* App Store + Google Play listing copy */}
        <section className="mb-8" data-testid="mobile-launch-listing-copy">
          <h2 className="font-spectral text-3xl tracking-tight mb-2 flex items-center gap-3">
            <Books size={28} weight="duotone" />
            App Store + Google Play listing copy
          </h2>
          <p className="text-sm text-stone-600 mb-6">
            Submit-ready copy for both stores, opening with the locked
            tagline so it indexes into iPhone Spotlight Search results. Each
            block has a character-budget readout against Apple&apos;s and
            Google&apos;s limits, plus a one-tap copy button.
          </p>
          {Object.entries(STORE_LISTING).map(([key, block]) => {
            const apple = budgetLabel(block.value, block.apple_limit);
            const google = budgetLabel(block.value, block.google_limit);
            const onCopy = async () => {
              try {
                await navigator.clipboard.writeText(block.value);
                toast.success(`Copied ${block.label.split(" (")[0]}`);
              } catch {
                toast.error("Could not copy. Select and copy manually.");
              }
            };
            return (
              <div
                key={key}
                className="rounded-xl border border-[#E5E5E5] bg-white p-5 mb-3"
                data-testid={`store-listing-${key}`}
              >
                <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
                  <p className="text-xs uppercase tracking-wider text-stone-500 font-medium">
                    {block.label}
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    {apple && (
                      <span
                        className={`text-[10px] font-mono px-2 py-0.5 rounded ${
                          apple.over ? "bg-red-100 text-red-800" : "bg-stone-100 text-stone-700"
                        }`}
                      >
                        Apple {apple.label}
                      </span>
                    )}
                    {google && (
                      <span
                        className={`text-[10px] font-mono px-2 py-0.5 rounded ${
                          google.over ? "bg-red-100 text-red-800" : "bg-stone-100 text-stone-700"
                        }`}
                      >
                        Google {google.label}
                      </span>
                    )}
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={onCopy}
                      className="h-7 px-2 text-xs"
                      data-testid={`store-listing-${key}-copy`}
                    >
                      <Copy size={12} className="mr-1" /> Copy
                    </Button>
                  </div>
                </div>
                <p className="text-sm text-stone-800 leading-relaxed font-mono whitespace-pre-wrap">
                  {block.value}
                </p>
              </div>
            );
          })}
        </section>

        {/* Icon + splash preview */}
        <section className="mb-8" data-testid="mobile-launch-branding-preview">
          <h2 className="font-spectral text-3xl tracking-tight mb-2 flex items-center gap-3">
            <Sparkle size={28} weight="duotone" />
            Icon &amp; splash preview &mdash; approve, or reply with edits
          </h2>
          <p className="text-sm text-stone-600 mb-6">
            Lock these now. Once a build is submitted with these assets,
            changing either forces a fresh build + another 24–48h Apple
            review. The mocks below are vector — final assets are rendered
            to 1024&times;1024 (icon) and 2732&times;2732 (splash) PNGs by
            the build pipeline.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div
              className="rounded-2xl border border-[#E5E5E5] bg-[#1A1614] p-6 flex flex-col items-center"
              data-testid="mobile-launch-icon-preview-card"
            >
              <p className="text-xs uppercase tracking-wider text-stone-400 mb-4 self-start">
                App icon (1024 &times; 1024)
              </p>
              <img
                src="/app-icon-1024.png"
                alt="Armstrong K9 Registry app icon"
                className="w-48 h-48 rounded-3xl shadow-2xl"
                data-testid="mobile-launch-icon-img"
              />
              <p className="text-xs text-stone-400 mt-4 text-center">
                Your real logo. Solid #1A1614 background (no alpha). iOS
                automatically applies the rounded-corner mask at install.
              </p>
            </div>
            <SplashPicker progress={progress} toggle={toggle} />
          </div>
          <div className="mt-5 flex flex-wrap gap-3">
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full h-11 px-6"
              data-testid="mobile-launch-approve-branding-btn"
              onClick={() => toggle("pf_branding")}
            >
              <CheckCircle size={16} weight="bold" className="mr-2" />
              {progress.pf_branding ? "Approved — locked" : "Approve both"}
            </Button>
            <Button
              variant="outline"
              className="rounded-full h-11 px-6"
              onClick={() => {
                const a = document.createElement("a");
                a.href = "/app-icon-1024.png";
                a.download = "armstrong-k9-app-icon-1024.png";
                a.click();
              }}
              data-testid="mobile-launch-download-icon-btn"
            >
              Download icon PNG
            </Button>
            <Button
              variant="outline"
              className="rounded-full h-11 px-6"
              onClick={() => {
                const slug = localStorage.getItem("admin_mobile_launch_splash_slug") || "v1";
                const a = document.createElement("a");
                a.href = `/app-splash-2732-${slug}.png`;
                a.download = `armstrong-k9-splash-2732-${slug}.png`;
                a.click();
              }}
              data-testid="mobile-launch-download-splash-btn"
            >
              Download chosen splash PNG
            </Button>
          </div>
        </section>

        {/* Apple Entitlement demo video */}
        <section className="mb-8" data-testid="mobile-launch-demo-video">
          <h2 className="font-spectral text-3xl tracking-tight mb-2 flex items-center gap-3">
            <DeviceMobile size={28} weight="duotone" />
            Reader-app demo video &mdash; for Apple Entitlement review
          </h2>
          <p className="text-sm text-stone-600 mb-6">
            Apple sometimes asks for a 30-second screen recording proving
            the external-link surface exists and links out to the system
            browser (not in-app). Pre-recorded against the live deploy at
            iPhone dimensions. Send Apple this file when they ask &mdash;
            often shortens the entitlement review by 3–5 days.
          </p>
          <div className="rounded-2xl border border-[#E5E5E5] bg-white p-6">
            <video
              src="/apple-entitlement-demo.webm"
              controls
              className="w-full max-w-sm mx-auto rounded-lg shadow-lg"
              data-testid="mobile-launch-demo-video-player"
              preload="metadata"
            />
            <div className="mt-4 flex flex-wrap gap-3 justify-center">
              <Button
                variant="outline"
                className="rounded-full h-11 px-6"
                onClick={() => {
                  const a = document.createElement("a");
                  a.href = "/apple-entitlement-demo.webm";
                  a.download = "armstrong-k9-reader-app-demo.webm";
                  a.click();
                }}
                data-testid="mobile-launch-download-video-btn"
              >
                Download .webm
              </Button>
            </div>
          </div>
        </section>

        {/* App Store screenshots gallery */}
        <section className="mb-8" data-testid="mobile-launch-screenshots">
          <h2 className="font-spectral text-3xl tracking-tight mb-2 flex items-center gap-3">
            <ImagesSquare size={28} weight="duotone" />
            App Store screenshots &mdash; 5 panels at Apple&apos;s 6.7&quot; size
          </h2>
          <p className="text-sm text-stone-600 mb-6">
            Pre-rendered at <span className="font-mono">1290 &times; 2796</span>{" "}
            pixels (Apple&apos;s required 6.7&quot; iPhone size). Watermarks
            stripped. Submit-ready. Download all 5 and upload them to App
            Store Connect when Apple verifies your account.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5" data-testid="mobile-launch-screenshots-grid">
            {[
              { slug: "01-registry", label: "Registry" },
              { slug: "02-pedigree", label: "Pedigree" },
              { slug: "03-marketplace", label: "Marketplace" },
              { slug: "04-breeders", label: "Breeders" },
              { slug: "05-subscription", label: "Subscription" },
            ].map((s) => (
              <a
                key={s.slug}
                href={`/app-store-screenshots/${s.slug}.png`}
                target="_blank"
                rel="noopener noreferrer"
                className="block group"
                data-testid={`mobile-launch-screenshot-${s.slug}`}
              >
                <div className="rounded-2xl overflow-hidden border border-[#E5E5E5] bg-[#0E0E0E] aspect-[1290/2796] hover:border-[#C9A227] transition-colors">
                  <img
                    src={`/app-store-screenshots/${s.slug}.png`}
                    alt={`${s.label} screenshot`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <p className="text-xs text-center mt-2 text-stone-600 group-hover:text-stone-900 font-medium">
                  {s.label}
                </p>
              </a>
            ))}
          </div>
          <div className="flex flex-wrap gap-3 justify-center">
            <Button
              variant="outline"
              className="rounded-full h-11 px-6"
              data-testid="mobile-launch-download-all-screenshots-btn"
              onClick={() => {
                [
                  "01-registry",
                  "02-pedigree",
                  "03-marketplace",
                  "04-breeders",
                  "05-subscription",
                ].forEach((slug, i) => {
                  setTimeout(() => {
                    const a = document.createElement("a");
                    a.href = `/app-store-screenshots/${slug}.png`;
                    a.download = `armstrong-k9-${slug}.png`;
                    a.click();
                  }, i * 250);
                });
              }}
            >
              Download all 5
            </Button>
            <Button
              variant="outline"
              className="rounded-full h-11 px-6"
              data-testid="mobile-launch-regenerate-screenshots-note"
              disabled
            >
              Auto-regenerated before each submission
            </Button>
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-8" data-testid="mobile-launch-faq">
          <h2 className="font-spectral text-3xl tracking-tight mb-2 flex items-center gap-3">
            <Question size={28} weight="duotone" />
            Common questions
          </h2>
          <div className="space-y-3 mt-6">
            {FAQ.map((item, i) => (
              <details
                key={i}
                className="rounded-xl border border-[#E5E5E5] bg-white p-5 group"
                data-testid={`mobile-launch-faq-${i}`}
              >
                <summary className="cursor-pointer font-medium text-sm flex items-center justify-between">
                  {item.q}
                  <span className="text-stone-400 group-open:rotate-45 transition-transform text-xl">
                    +
                  </span>
                </summary>
                <p className="text-sm text-stone-700 mt-3 leading-relaxed">
                  {item.a}
                </p>
              </details>
            ))}
          </div>
        </section>

        {/* What to do now */}
        <section
          className="rounded-2xl bg-[#0E0E0E] text-white p-8"
          data-testid="mobile-launch-next-actions"
        >
          <h2 className="font-spectral text-2xl tracking-tight mb-3">
            What to do right now
          </h2>
          <ol className="list-decimal list-outside pl-5 space-y-2 text-sm">
            <li>Start the Apple Developer enrollment (Step 1 above). It&apos;s the longest because of the 24–48h verification window.</li>
            <li>Pay $25 for Google Play (Step 2). Instant.</li>
            <li>Click &ldquo;Save to GitHub&rdquo; in the Emergent chat input below (Step 3). 5 minutes.</li>
            <li>Create the Firebase project once Apple verification is done (Step 4).</li>
            <li>
              Reply in chat with the deliverables. I start the build pipeline that day.
            </li>
          </ol>
          <p className="text-xs text-stone-400 mt-5">
            Total time on your end this week: ~40 minutes spread across two days. Mostly waiting for Apple&apos;s verification email.
          </p>
        </section>

        <p className="text-xs text-stone-500 text-center mt-8">
          This page was generated for your account. Bookmark it, or come back via Admin Console → &ldquo;Launch on the App Stores.&rdquo;
        </p>
      </div>
    </AppShell>
  );
}
